import { useEffect, useState, useCallback } from "react";
import { X, ChevronLeft, ChevronRight, Download, Share2 } from "lucide-react";
import ShareButton from "../ShareButton";
import { shareFile } from "../../lib/shareFile";
import { useShareable } from "../../hooks/useShareable";

/**
 * MediaLightbox — Wave 19 #88.
 *
 * WhatsApp-style full-screen image / video viewer with arrow scrolling
 * between media in the same chat, ESC to close, and a download action.
 *
 * Props:
 *   items:      [{url, filename, kind ('image'|'video'), msgId}]
 *   activeId:   currently-shown msgId
 *   onClose:    () => void
 */
export default function MediaLightbox({ items, activeId, onClose }) {
  const [idx, setIdx] = useState(() => Math.max(0, items.findIndex((i) => i.msgId === activeId)));

  useEffect(() => {
    const i = items.findIndex((it) => it.msgId === activeId);
    if (i >= 0) setIdx(i);
  }, [activeId, items]);

  const next = useCallback(() => setIdx((v) => Math.min(items.length - 1, v + 1)), [items.length]);
  const prev = useCallback(() => setIdx((v) => Math.max(0, v - 1)), []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev, onClose]);

  const item = items[idx];
  const shareDesc = item ? {
    url: item.url,
    filename: item.filename,
    contentType: item.content_type,
    title: item.filename || "shared media",
  } : null;
  const { handlers: shareHandlers, menu: shareMenu } = useShareable(shareDesc);

  if (!items.length || !item) return null;

  return (
    <div
      className="fixed inset-0 bg-black/95 z-[100] flex flex-col hv-safe-top hv-safe-bottom hv-safe-x"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      data-testid="media-lightbox"
      role="dialog"
      aria-modal="true"
    >
      {/* Top bar — Close on the LEFT to match JC photo view (2026-02-20 fix:
          previously anchored to `absolute top-0` which slipped under the
          mobile status bar / URL bar and made Close+Download un-tappable). */}
      <div
        className="flex items-center justify-between px-3 py-2 border-b border-white/10 text-white"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="hv-btn hv-btn-ghost text-xs bg-white/10 text-white border-white/20 hover:bg-white/20"
          data-testid="media-lightbox-close"
          type="button"
        >
          <X className="w-4 h-4" /> Close
        </button>
        <div className="text-xs text-white/70 tabular-nums truncate flex-1 text-center px-2">
          {items.length > 1 && (<span>{idx + 1} / {items.length}</span>)}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              shareFile(shareDesc);
            }}
            className="hv-btn hv-btn-ghost text-xs bg-white/10 text-white border-white/20 hover:bg-white/20"
            title="Share file directly"
            data-testid="media-lightbox-share-file"
            aria-label="Share file"
          >
            <Share2 className="w-4 h-4" />
          </button>
          {item.uploadId && (
            <ShareButton
              kind="upload"
              refId={item.uploadId}
              title={item.filename}
              downloadUrl={item.url}
              compact
              testId="media-lightbox-share"
              className="bg-white/10 text-white border-white/20 hover:bg-white/20 rounded p-1.5"
            />
          )}
          <a
            href={item.url}
            download={item.filename || "download"}
            target="_blank"
            rel="noopener noreferrer"
            className="hv-btn hv-btn-ghost text-xs bg-white/10 text-white border-white/20 hover:bg-white/20"
            data-testid="media-lightbox-download"
            onClick={(e) => e.stopPropagation()}
            aria-label="Download"
          >
            <Download className="w-4 h-4" />
          </a>
        </div>
      </div>

      {/* Media */}
      <div
        className="flex-1 flex items-center justify-center px-4 min-h-0 relative"
        onClick={(e) => e.stopPropagation()}
      >
        {item.kind === "image" ? (
          <img
            src={item.url}
            alt={item.filename}
            className="max-w-full max-h-full object-contain"
            data-testid="media-lightbox-image"
            draggable={false}
            {...shareHandlers}
          />
        ) : (
          <video
            src={item.url}
            controls
            autoPlay
            className="max-w-full max-h-full"
            data-testid="media-lightbox-video"
            {...shareHandlers}
          />
        )}
        {/* Nav arrows */}
        {items.length > 1 && (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); prev(); }}
              disabled={idx === 0}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/60 hover:bg-black/80 border border-white/20 text-white flex items-center justify-center disabled:opacity-30"
              data-testid="media-lightbox-prev"
              aria-label="Previous"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); next(); }}
              disabled={idx === items.length - 1}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/60 hover:bg-black/80 border border-white/20 text-white flex items-center justify-center disabled:opacity-30"
              data-testid="media-lightbox-next"
              aria-label="Next"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </>
        )}
      </div>
      {/* Filename footer — kept tiny so it never blocks the image. */}
      {item.filename && (
        <div
          className="px-4 py-2 text-center text-[11px] text-white/60 truncate"
          onClick={(e) => e.stopPropagation()}
        >
          {item.filename}
        </div>
      )}
      {shareMenu}
    </div>
  );
}
